import type {
  AddEmployeeRequest,
  UpdateEmployeeRequest,
} from "features/Employees/types/employee.types";

/** Raw form values (no File fields). */
export type EmployeeFormValuesRest = {
  userId: string | number;
  /** Legacy form fields; no longer collected in employee form. */
  firstName?: string;
  /** Legacy form fields; no longer collected in employee form. */
  lastName?: string;
  /** Legacy form fields; no longer collected in employee form. */
  email?: string;
  /** Legacy form fields; no longer collected in employee form. */
  phone?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  bloodGroup: string;
  departmentId: string | number;
  designationId: string | number;
  employmentCategoryId: string | number;
  joinDate: string;
};

function strOrNull(raw: string | undefined | null): string | null {
  if (raw == null || raw === "") return null;
  const t = String(raw).trim();
  return t === "" ? null : t;
}

export function buildEmployeeRequestFromFormValues(
  values: EmployeeFormValuesRest,
): AddEmployeeRequest {
  return {
    userId: Number(values.userId),
    departmentId:
      values.departmentId != null && values.departmentId !== ""
        ? Number(values.departmentId)
        : null,
    designationId:
      values.designationId != null && values.designationId !== ""
        ? Number(values.designationId)
        : null,
    employmentCategoryId: Number(values.employmentCategoryId),
    joinDate: values.joinDate,
    address: strOrNull(values.address),
    city: strOrNull(values.city),
    state: strOrNull(values.state),
    pincode: strOrNull(values.pincode),
    bloodGroup: strOrNull(values.bloodGroup),
  };
}

export function employeeRequestToApiFields(
  payload: AddEmployeeRequest | UpdateEmployeeRequest,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};

  if ("id" in payload && payload.id != null && Number(payload.id) > 0) {
    out.id = Number(payload.id);
  }

  if (payload.userId != null && Number.isFinite(Number(payload.userId))) {
    out.user_id = Number(payload.userId);
  }

  if (payload.firstName != null) {
    out.first_name = payload.firstName;
  }

  if (payload.lastName != null) {
    out.last_name = payload.lastName;
  }

  if (payload.email != null) {
    out.email = payload.email;
  }

  if (payload.phone != null) {
    out.phone = payload.phone;
  }

  if (payload.departmentId != null) {
    out.department_id = Number(payload.departmentId);
  }

  if (payload.designationId != null) {
    out.designation_id = Number(payload.designationId);
  }

  out.employment_category_id = Number(payload.employmentCategoryId);

  if (payload.joinDate != null && String(payload.joinDate).trim() !== "") {
    out.join_date = String(payload.joinDate).trim();
  }

  if (payload.address != null) {
    out.address = payload.address;
  }

  if (payload.city != null) {
    out.city = payload.city;
  }

  if (payload.state != null) {
    out.state = payload.state;
  }

  if (payload.pincode != null) {
    out.pincode = payload.pincode;
  }

  if (payload.bloodGroup != null) {
    out.blood_group = payload.bloodGroup;
  }

  return out;
}

/** Multipart body: snake_case fields + optional `avatar` file. */
export function employeeRequestToFormData(
  payload: AddEmployeeRequest | UpdateEmployeeRequest,
  avatarFile: File | null,
): FormData {
  const fd = new FormData();
  const fields = employeeRequestToApiFields(payload);
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    fd.append(key, String(value));
  }
  if (avatarFile instanceof File) {
    fd.append("avatar", avatarFile);
  }
  return fd;
}
